import { Button, Input, Tooltip } from 'antd';
import { MouseEvent, useState } from 'react';
import CopyIcon from '../../../assets/icon/file_copy.svg?react';
import styles from './CopyableInput.module.scss';

const COPY_STATUS = {
  IDLE: 'idle',
  COPIED: 'copied',
  FAILED: 'failed',
} as const;

type CopyStatus = (typeof COPY_STATUS)[keyof typeof COPY_STATUS];

const TOOLTIP_TEXT: Record<CopyStatus, string> = {
  [COPY_STATUS.IDLE]: 'Copy',
  [COPY_STATUS.COPIED]: 'Copied to clipboard',
  [COPY_STATUS.FAILED]: 'Could not copy to clipboard',
};

interface CopyableInputProps {
  value: string;
  id?: string;
  copyButtonId?: string;
}

export const CopyableInput = ({ value, id, copyButtonId }: CopyableInputProps) => {
  const [copyStatus, setCopyStatus] = useState<CopyStatus>(COPY_STATUS.IDLE);

  const handleCopy = async (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    try {
      await navigator.clipboard.writeText(value);
      setCopyStatus(COPY_STATUS.COPIED);
    } catch {
      setCopyStatus(COPY_STATUS.FAILED);
    }
  };

  const handleTooltipAfterOpenChange = (isOpen: boolean) => {
    if (!isOpen) setCopyStatus(COPY_STATUS.IDLE);
  };

  return (
    <Input
      id={id}
      className={styles.input}
      value={value}
      readOnly
      suffix={
        <Tooltip title={TOOLTIP_TEXT[copyStatus]} afterOpenChange={handleTooltipAfterOpenChange}>
          <Button
            id={copyButtonId}
            className={styles.copyButton}
            color="default"
            variant="link"
            size="small"
            aria-label="Copy to clipboard"
            icon={<CopyIcon />}
            onClick={handleCopy}
          />
        </Tooltip>
      }
    />
  );
};
